import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Gamepad, OrigamiIcon, SliceIcon } from 'lucide-react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'; // Added react-heroicons import
import ClaudeIcon from '../../../Icons/Casino';
import GameIcon from '../../../Icons/Game';
import LotteryIcon from '../../../Icons/Lottery';
import TradingIcon from '../../../Icons/Crypto';
import PromotionIcon from '../../../Icons/PromotionIcon';
import VipSvg from '../../../Icons/Vip';
import BonusIcon from '../../../Icons/BonusIcon';
import AffilateIcon from '../../../Icons/AffliateIcon';
import ProvablyFairIcon from '../../../Icons/Provably';
import HeadphoneIcon from '../../../Icons/Support';
import GlobeIcon from '../../../Icons/Glob';
import SkylaSlot from '../../../Icons/SlotsSkyla';
import SkylaLivecasino from '../../../Icons/SkylaLiveCasino';
import ClubIconMember from '../../../Icons/Club';
import { CRISTP_ID, addNewStyle, getUID, wait, sendNotfication, API } from '../../../Helper'; // Ensure addNewStyle and wait are imported
import SlotsIcon from '../../../Icons/SlotsIcon';
import CrashIcon from '../../../Icons/CrashIcon';
import { Android } from '@mui/icons-material';
import { useGameLaunch } from '../../../Context/GameLaunchContext';
import { launchGame } from '../../../services/gameLaunchService';
import { launchHuiduGame } from '../../../services/huiduGameService';
import { useSelector } from 'react-redux';

const SidebarContainer = styled.div`
  width: 100%;
  transition: width 0.3s ease;
  overflow-y: auto;
  color: white;
  padding-top: 46px;
  display: flex;
  flex-direction: column;
  height: 100%; /* Ensure it takes full height */
`;

const MenuSection = styled.div`
  padding: 12px;
  margin-top: -55px;
`;

const MenuSectionS = styled.div`
  padding: 6px 0px;
  width: 100%;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  text-decoration: none;
  color: #808A94;
  border-radius: ${props => {
    if (props.accordion) {
      return props.isAccordionOpen ? '12px 12px 0 0' : '12px';
    }
    return '12px';
  }};
  margin-bottom: 4px;
  background-color: #333839;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(90deg, rgba(35, 238, 136, 0.2), rgba(35, 238, 136, 0)) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    color: white;
  }
`;

const MenuItemVip = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #808A94;
  margin: 4px 0px;
  width: 100%;
  border-radius: 12px;
  padding: 8px 12px;
  background-color: ${props => props.isActive ? '#3A4142' : '#333839'};
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(90deg, rgba(35, 238, 136, 0.2), rgba(35, 238, 136, 0)) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
  }
`;

const BottomMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  text-decoration: none;
  color: #808A94;
  margin: 4px 0px;
  border-radius: 12px;
  background-color: #333839;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: linear-gradient(90deg, rgba(35, 238, 136, 0.2), rgba(35, 238, 136, 0)) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    color: white;
  }
`;

const OuterWrapper = styled(Link)`
  display: flex;
  align-items: center;
  color: #808A94;
  border-radius: 12px;
  transition: all 0.2s ease;
`;

const IconBgNew = styled.div`
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #4a5354;
  }
`;

const MenuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || 'inherit'};
  transition: color 0.2s ease;

  ${MenuItem}:hover &,
  ${MenuItemVip}:hover &,
  ${BottomMenuItem}:hover & {
    color: #ffffff;
  }
`;

const VIPSpan = styled.span`
  color: #25ee89;
  &:hover {
    color: white !important;
  }
`;

const ClubSpan = styled.span`
  color: white;
  &:hover {
    color: white !important;
  }
`;

const VIPContainer = styled.div`
  display: flex;
  gap: 2px;

  ${MenuItem}:hover &,
  ${MenuItemVip}:hover & ${ClubSpan} {
    color: #25ee89;
  }
`;

const MenuText = styled.span`
  display: ${props => props.isCollapsed ? 'none' : 'block'};
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.isActive ? '#25ee89' : 'white'};
  white-space: nowrap;
  transition: color 0.2s ease;

  ${MenuItem}:hover &,
  ${MenuItemVip}:hover &,
  ${BottomMenuItem}:hover & {
    color: white !important;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #3a3f40;
  margin: 12px 4px;
`;

const ExternalIndicator = styled.div`
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
`;

const IconBg = styled.div`
  padding: 6px 8px;
  background-color: #394142;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #4a5354;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  width: ${props => props.showFull ? '128px' : '96px'};
  height: auto;
  transition: width 0.3s ease;
`;

const SubMenuContainer = styled.div`
  margin-top: -6px;
  background: #232626;
  display: ${props => props.isOpen ? 'block' : 'none'};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const SubMenuItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  text-decoration: none;
  color: #808A94;
  border-radius: 8px;
  margin-bottom: 4px;
  background-color: ${props => props.isActive ? '#3A4142' : '#333839'};
  transition: all 0.2s ease;

  &:hover {
    background-color: #3A4142;
    color: #ffffff;
  }
`;

const MenuItemClub = styled(MenuItemVip)`
  color: #25ee89;
  margin: 4px 0px;

  ${props => props.isActive && `
    background: linear-gradient(90deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0)) !important;
    background-color: rgba(255, 215, 0, 0.05) !important;
  `}

  ${MenuText} {
    color: #25ee89;
  }

  ${MenuIcon} {
    color: #25ee89;
  }
`;

const BottomSection = styled.div`
  padding: 8px;
  margin-top: auto; /* Pushes this section to the bottom */
`;

const SkylaMobileMenu = ({ isCollapsed }) => {
  const [activePath, setActivePath] = useState('/');
  const [openAccordions, setOpenAccordions] = useState({});
  const [openSGOriginals, setSGOriginals] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [show, setshow] = useState(true);
  const { setGameUrl, setIsGameOpen, setIsLuckySportGame } = useGameLaunch();

  useLayoutEffect(() => {
    setOpenAccordions(prev => ({ ...prev }));
  }, [isCollapsed]);

  const toggleAccordion = (path) => {
    setOpenAccordions((prevState) => ({
      ...prevState,
      [path]: !prevState[path],
    }));
  };

  const userInfo = useSelector((state) => state.userInfo.Userinfo);
  const finalState = useSelector((state) => state.finalState.finalState);
        console.log("userInfo", userInfo);
  
        console.log("hjvjhvghchgcgfx",show);
  
          const getUserInfoagent = (data) => {
    // Always show all options for all users
    setshow(true);
      };
    useEffect(() => {
      getUserInfoagent(userInfo);
  
    }, [userInfo]);

  const handleSportsClick = async () => {
    try {
      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'INR';
    
       const id = getUID();
            const gameUrl = await launchGame(
              '92b24e4c25107367a80e0fe1a97c24e4', 
              id, 
              coins,
              credits
            );
  
      if (gameUrl) {
        setGameUrl(gameUrl);
        setIsGameOpen(true);
      } else {
        console.error('Failed to get game URL');
      }
    } catch (error) {
      console.error('Failed to launch sports game:', error);
    }
  };

  const handleSportBookClick = async () => {
    try {
      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'INR';

      const id = getUID();
      const result = await launchHuiduGame(
        '8a704858d5deb4af1ddc722092ac7614', // BETBY game UID
        id,
        credits,
        coins
      );

      if (result && result.game_launch_url) {
        setGameUrl(result.game_launch_url);
        setIsGameOpen(true);
      } else {
        sendNotfication('Failed to launch Sport Book', 'error');
      }
    } catch (error) {
      console.error('Failed to launch sport book game:', error);
      sendNotfication(error.message || 'Failed to launch Sport Book', 'error');
    }
  };

  const handleLuckSportClick = async () => {
    try {
      const id = getUID();
      
      if (!id) {
        sendNotfication('Please login to play LUCKSPORT', 'error');
        return;
      }

      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'INR';

      const result = await launchHuiduGame(
        '92b24e4c25107367a80e0fe1a97c24e4', // LUCKSPORT game UID
        id,
        credits,
        coins
      );

      if (result && result.game_launch_url) {
        setGameUrl(result.game_launch_url);
        setIsLuckySportGame(true);
        setIsGameOpen(true);
      } else {
        sendNotfication('Failed to launch LUCKSPORT', 'error');
      }
    } catch (error) {
      console.error('Failed to launch LUCKSPORT game:', error);
      sendNotfication(error.message || 'Failed to launch LUCKSPORT', 'error');
    }
  };

  const handleEsportsClick = async () => {
    try {
      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'INR';
      const id = getUID();
      if (!id) {
        sendNotfication('Please login to play E-Sports', 'error');
        return;
      }
      const result = await launchHuiduGame(
        '4ee8e0051a035b463b47c3c473ce317d', // TFGaming/Esports game UID
        id,
        credits,
        coins
      );
      if (result && result.game_launch_url) {
        setGameUrl(result.game_launch_url);
        setIsGameOpen(true);
      } else {
        sendNotfication('Failed to launch E-Sports', 'error');
      }
    } catch (error) {
      console.error('Failed to launch esports game:', error);
      sendNotfication(error.message || 'Failed to launch E-Sports', 'error');
    }
  };

  const handle9WicketClick = async () => {
    try {
      const id = getUID();
      if (!id) {
        sendNotfication('Please login to play 9Wicket', 'error');
        return;
      }

      // Show loader immediately while API call is in progress
      setIsGameOpen(true);

      const coins = localStorage.getItem('coin') || 'INR';

      const response = await fetch(`${API}/ninewicket/game/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: id,
          currency_code: coins,
          language: 'en'
        })
      });

      const data = await response.json();

      if (data.success && data.data && data.data.game_launch_url) {
        setGameUrl(data.data.game_launch_url);
      } else {
        const errorMsg = data.error || data.message || 'Failed to launch 9Wicket';
        setIsGameOpen(false);
        sendNotfication(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Failed to launch 9Wicket:', error);
      setIsGameOpen(false);
      sendNotfication('Failed to launch 9Wicket: ' + error.message, 'error');
    }
  };

  const handleMenuClick = (e, item) => {
    // if (item.accordion) {
    //   e.preventDefault();
    //   toggleAccordion(item.path);
    // }

    // Handle specific menu item clicks
    switch (item.text) {
      case 'Sports Exchange':
        e.preventDefault();
        handleLuckSportClick();
        break;
      case 'E-Sports':
        e.preventDefault();
        handleEsportsClick();
        break;
      case 'Sport Book':
        e.preventDefault();
        handleSportBookClick();
        break;
      case '9Wicket':
        e.preventDefault();
        handle9WicketClick();
        break;
      case 'Casino':
        window.location.href = '/casino';
        break;
      default:
        break;
    }

    setActivePath(item.path);
  };

  const handleSupportClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.Tawk_API && window.Tawk_API.maximize) {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
    }
  }, []);

  const handleDownloadAPK = () => {
    window.open('https://your-app-download-url.apk', '_blank');
  };

  const menuItems = [
    { id: 1, icon: <ClaudeIcon />, text: 'Casino', path: '/casino', accordion: false },
    { id: 8, icon: <GameIcon />, text: 'Sports Exchange', path: '#', accordion: false },
    // { id: 7, text: 'Sport Book', path: '#', accordion: false },
      {id: 2,
          icon: <svg width="20" height="20" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
 
  <circle cx="50" cy="50" r="45" fill="#25ee89"/>

 
  <path d="M50 5 A45 45 0 0 1 95 50 L85 45 L95 50 L85 55" fill="#fff"/>
  <path d="M95 50 A45 45 0 0 1 50 95 L55 85 L50 95 L45 85" fill="#fff"/>

  
  <circle cx="50" cy="50" r="20" fill="none" stroke="black" stroke-width="3"/>
  <path d="M35 50 A15 15 0 0 1 65 50 A15 15 0 0 1 35 50" fill="none" stroke="black" stroke-width="3"/>
</svg>,
          text: 'E-Sports',
          path: '#',
          accordion: false,
      
  },
    // { id: 10, text: '9Wicket', path: '#', accordion: false },
    { id: 3, icon: <LotteryIcon />, text: 'Spribe', path: '/spribe-games', accordion: false },
    { id: 646, icon: <CrashIcon />, text: 'Live', path: '/live', accordion: false },
    { id: 11, icon: <SlotsIcon />, text: 'Slots', path: '/pm-slots', accordion: false },
  ];

  const vipItems = [
    { id: 0, icon: <PromotionIcon />, text: 'Promotions', path: '/promotions', enabled: show },
    { id: 1, icon: <VipSvg />, text: 'VIP Club', path: '/vip-club', color: '#4CAF50', enabled: show },
    // { id: 2, icon: <ClubIconMember />, text: 'Club Membership', path: '/club-membership', color: '#4CAF50' },
    { id: 3, icon: <BonusIcon />, text: 'Bonus', path: '/bonus', enabled: show },
    { id: 847, icon: <AffilateIcon />, text: 'Affiliate', path: '/affiliate', enabled: show },
    { id: 5, icon: <ProvablyFairIcon />, text: 'Provably Fair', path: '/provably-fair' ,enabled: true },
   
  ];

  

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <MenuSection>
        {menuItems.map((item) => (
          <React.Fragment key={item.path}>
            <MenuItem
              to={item.path}
              accordion={item.accordion}
              isAccordionOpen={openAccordions[item.path]}
              isActive={activePath === item.path}
              isCollapsed={isCollapsed}
              onClick={(e) => handleMenuClick(e, item)} // Use the unified handler
            >
              <div style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <LeftSection>
                  <MenuIcon isCollapsed={isCollapsed} color={item.color}>
                    {item.icon}
                  </MenuIcon>
                  <MenuText
                    isActive={activePath === item.path}
                    isCollapsed={isCollapsed}
                    color={item.color}
                    isSpecial={item.text === 'VIP Club'}
                  >
                    {item.text === "VIP Club" ? (
                      <VIPContainer>
                        <VIPSpan>VIP</VIPSpan>
                        <ClubSpan>Club</ClubSpan>
                      </VIPContainer>
                    ) : item.text}
                  </MenuText>
                </LeftSection>
                {item.accordion && !isCollapsed && (
                  <IconBgNew>
                    <MenuIcon isCollapsed={isCollapsed}>
                      {openAccordions[item.path] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </MenuIcon>
                  </IconBgNew>
                )}
              </div>
            </MenuItem>
          </React.Fragment>
        ))}
      </MenuSection>

      <div style={{ padding: '8px', flex: 1 }}>
        <OuterWrapper>
          <MenuSectionS>
            {vipItems
      .filter(item => item.enabled).map((item, index) => (
              <React.Fragment key={item.path}>
                {item.text === 'Club Membership' ? (
                  <MenuItemClub
                    to={item.path}
                    isActive={activePath === item.path}
                    isCollapsed={isCollapsed}
                    onClick={() => setActivePath(item.path)}
                  >
                    <LeftSection>
                      <MenuIcon isCollapsed={isCollapsed} color="#25ee89">
                        {item.icon}
                      </MenuIcon>
                      <MenuText
                        isActive={activePath === item.path}
                        isCollapsed={isCollapsed}
                        color="#25ee89"
                      >
                        {item.text}
                      </MenuText>
                    </LeftSection>
                  </MenuItemClub>
                ) : (
                  <MenuItemVip
                    to={item.path}
                    isActive={activePath === item.path}
                    isCollapsed={isCollapsed}
                    onClick={() => setActivePath(item.path)}
                  >
                    <LeftSection>
                      <MenuIcon isCollapsed={isCollapsed} color={item.color}>
                        {item.icon}
                      </MenuIcon>
                      <MenuText
                        isActive={activePath === item.path}
                        isCollapsed={isCollapsed}
                        color={item.color}
                        isSpecial={item.text === 'VIP Club'}
                      >
                        {item.text === "VIP Club" ? (
                          <VIPContainer>
                            <VIPSpan>VIP</VIPSpan>
                            <ClubSpan>Club</ClubSpan>
                          </VIPContainer>
                        ) : item.text}
                      </MenuText>
                    </LeftSection>
                    {item.external && (
                      <ExternalIndicator isCollapsed={isCollapsed}>
                        <GlobeIcon size={16} />
                      </ExternalIndicator>
                    )}
                  </MenuItemVip>
                )}
                {[4, 7, 8, 12].includes(index) && <Divider />}
              </React.Fragment>
            ))}
          </MenuSectionS>
        </OuterWrapper>
      </div>

      {/* Live Support hidden */}
      <BottomSection>
        <BottomMenuItem onClick={handleSupportClick}>
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <LeftSection>
              <MenuIcon isCollapsed={isCollapsed}>
                <HeadphoneIcon />
              </MenuIcon>
              <MenuText isCollapsed={isCollapsed}> 
                Live Support 
                
              </MenuText>
            </LeftSection>
          </div>
        </BottomMenuItem>
      </BottomSection>

      {/* WhatsApp & Telegram */}
      {(finalState?.whatsapp_enabled || finalState?.telegram_enabled) && (
        <div style={{ padding: '0 8px 16px' }}>
          {finalState?.whatsapp_enabled && finalState?.whatsapp_number && (
            <a
              href={`https://wa.me/${finalState.whatsapp_number}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', marginBottom: '4px', borderRadius: '12px', background: 'rgba(55, 65, 70, 0.6)', textDecoration: 'none' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>WhatsApp</span>
            </a>
          )}
          {finalState?.telegram_enabled && finalState?.telegram_username && (
            <a
              href={`https://t.me/${finalState.telegram_username}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '12px', background: 'rgba(55, 65, 70, 0.6)', textDecoration: 'none' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#0088cc"/><path d="M9.04 16.14l-.36-3.26 5.74-5.18c.25-.23-.05-.35-.39-.14l-7.09 4.47-3.06-.96c-.66-.18-.67-.63.15-.95l11.97-4.61c.55-.25 1.07.13.86.95l-2.04 9.6c-.14.65-.56.81-1.13.5l-3.12-2.3-1.51 1.46c-.17.17-.31.31-.56.31z" fill="#fff"/></svg>
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Telegram</span>
            </a>
          )}
        </div>
      )}
     
    </SidebarContainer>
  );
};

export default SkylaMobileMenu;